import axios from 'axios';
import qs from 'querystring';
import get from 'lodash/get';
import FormData from 'form-data';
import { v4 as uuid } from 'uuid';

import sanitizeArticleUserStories from 'state/sanitizers/sanitizeArticleUserStories';
import sanitizeArticleRating from 'state/sanitizers/sanitizeArticleRating';
import sanitizeJobPostings from 'state/sanitizers/sanitizeJobPostings';

import HttpStatusCodes from 'constants/HttpStatusCodes';
import {
  FormValues,
  ArticleUserStory,
  ArticleRating,
  SurveyResponse,
  SurveyResponseRequestBody,
  SurveyResponseRequestBodyQuestion,
} from 'types';

const BuoyClient: {
  getArticleUserStories(slug: string): Promise<ArticleUserStory[]>;
  getArticleRating(slug: string): Promise<ArticleRating>;
  postArticleRating(slug: string, isUpvote: boolean): Promise<any>;
  postArticleUserStory(
    slug: string,
    id: string,
    formValues: FormValues
  ): Promise<any>;
  postWebToLeadForm(
    form: FormValues,
    formLeadSource?: string,
    formLeadSourceOtherC?: string
  ): Promise<any>;
  postGeneralInquiry(form: FormValues): Promise<any>;
  getJobPostings(): Promise<any>;
  createSurveyResponse(
    surveyPageId: string,
    surveyResponse: SurveyResponse
  ): Promise<any>;
} = {
  async getArticleUserStories(slug) {
    const response = await axios
      .get(`${process.env.getArticleUserStoryRequestUrl}${slug}.json`)
      .catch((err) => console.error(err));

    return sanitizeArticleUserStories(get(response, 'data'));
  },
  async getArticleRating(slug) {
    const bodyFormData = new FormData();
    bodyFormData.append('slug', slug);

    const response = await axios({
      method: 'get',
      url: process.env.getArticleRatingRequestUrl,
      data: bodyFormData,
      headers: { 'Content-Type': 'multipart/form-data' },
    }).catch((err) => console.error(err));

    return sanitizeArticleRating(get(response, 'data'));
  },
  async postArticleRating(slug, isUpvote) {
    const bodyFormData = new FormData();
    const upvote = isUpvote ? 'True' : 'False';

    bodyFormData.append('slug', slug);
    bodyFormData.append('is_upvote', upvote);

    const response = await axios({
      method: 'post',
      url: process.env.postArticleRatingRequestUrl as string,
      data: bodyFormData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return get(response, 'data', '');
  },
  async postArticleUserStory(slug, id, form) {
    // Preserve whitespace from textarea
    const bodyText = get(form, 'story', '')
      .split(/\r?\n/)
      .map((paragraph) => {
        return {
          _type: 'block',
          children: [
            {
              _type: 'span',
              marks: [],
              text: paragraph,
            },
          ],
          markDefs: [],
          style: 'normal',
        };
      });

    const documentStub = {
      _id: `drafts.${uuid()}`,
      _type: 'userStory',
      title: get(form, 'title'),
      email: get(form, 'email'),
      bodyText,
      firstNameInitial: get(form, 'name', '').charAt(0),
      article: {
        _ref: id,
        _type: 'reference',
      },
    };

    const sanityResponse = await axios
      .post('/api/sanity_documents', documentStub)
      .catch((err) => {
        console.error('Sanity error', err);
      });

    if (get(sanityResponse, 'status') === HttpStatusCodes.OK) {
      return get(sanityResponse, 'data', '');
    }

    // If submission to Sanity fails, we submit to Buoy API as backup
    const bodyFormData = new FormData();
    bodyFormData.append('slug', slug);
    Object.entries(form).forEach((value) =>
      bodyFormData.append(value[0], value[1])
    );

    const buoyResponse = await axios({
      method: 'post',
      url: process.env.postArticleUserStoryRequestUrl as string,
      data: bodyFormData,
      headers: { 'Content-Type': 'multipart/form-data' },
    }).catch((err) => Promise.reject(err));

    return get(buoyResponse, 'data', '');
  },
  async postWebToLeadForm(
    form,
    formLeadSource = 'Website',
    formLeadSourceOtherC
  ) {
    const requestBody = {
      ...form,
      oid: '00Df4000002cEZv',
      lead_source: formLeadSource,
      lead_source_other_c: formLeadSourceOtherC,
    };

    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    const response = await axios
      .post(
        process.env.postWebToLeadFormRequestUrl as string,
        qs.stringify(requestBody),
        config
      )
      .catch((err) => console.error(err));

    return get(response, 'data', '');
  },
  async postGeneralInquiry(form) {
    const response = await axios
      .post('/api/inquiries', form)
      .catch((err) => Promise.reject(err));

    return get(response, 'data', '');
  },
  async getJobPostings() {
    const response = await axios
      .get(`https://api.lever.co/v0/postings/buoyhealth`)
      .catch((err) => console.error(err));

    return sanitizeJobPostings(get(response, 'data'));
  },
  async createSurveyResponse(
    surveyPageId: string,
    surveyResponse: SurveyResponse
  ) {
    const questions = Object.keys(surveyResponse).reduce(
      (questionsBody: SurveyResponseRequestBodyQuestion[], questionId) => {
        const questionBody = {
          id: questionId,
          answers: [
            {
              choice_id: surveyResponse[questionId],
            },
          ],
        };

        if (!surveyResponse[questionId]) {
          return questionsBody;
        } else {
          return questionsBody.concat(
            questionBody as SurveyResponseRequestBodyQuestion
          );
        }
      },
      []
    );

    const requestBody: SurveyResponseRequestBody = {
      pages: [
        {
          id: surveyPageId,
          questions,
        },
      ],
    };

    const response = await axios
      .post('/api/survey_responses', requestBody)
      .catch((err) => Promise.reject(err));

    return get(response, 'data', '');
  },
};

export default BuoyClient;
